/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER, TIPOS_ACTIVO, DESTINO_TIPOS, getVidaUtilPorTipo } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import {
  crearActivo,
  eliminarActivo,
  actualizarActivo,
  getActivosParaVincularFactura,
  vincularFacturaActivo,
} from "../../utils/activos";
import { vincularFacturaRecibida, ignorarFacturaRecibida, reactivarFactura } from "../../utils/conciliacionSII";
import { esNotaCredito as esNotaCreditoDte } from "../../utils/dte";
import { facturaRecibidaEstaPagada } from "../../utils/estadoCuenta/facturaRecibidaPagada";
import { guardarFechaFactura } from "../../utils/guardarFechaFactura";
import { actualizarProveedor } from "../../utils/proveedores";
import { formatRut } from "../../utils/rut";
import { client } from "../../utils/supabase";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import ActivosCandidatosLista from "../FlujoCajaV2/ActivosCandidatosLista";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto) => {
  if (monto == null) return "-";
  return `$ ${Number(monto).toLocaleString("es-CL")}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

// Agrupar CUENTAS_ER por grupo
const CUENTAS_POR_GRUPO = {};
CUENTAS_ER.forEach((c) => {
  if (!CUENTAS_POR_GRUPO[c.grupo]) CUENTAS_POR_GRUPO[c.grupo] = [];
  CUENTAS_POR_GRUPO[c.grupo].push(c);
});

const LINEA_VACIA = {
  tipo_activo: "",
  descripcion: "",
  numero_serie: "",
  cantidad: 1,
  costo_unitario: 0,
  destino_tipo: "",
  destino_id: "",
  // Empleado usa columna aparte (uuid) — destino_id es INTEGER y no admite el uuid del empleado.
  destino_empleado_id: "",
};

// ─── Componente Modal ────────────────────────────────────

const AsignarRecibidaModal = ({ isOpen, toggle, factura, onAsignada, ctxPago }) => {
  const { pais, id: userId } = useAuthContext();

  // Nota de crédito recibida (tipo_dte=61): habilita el vínculo manual al documento que anula/ajusta.
  const esNotaCredito = esNotaCreditoDte(factura);

  // Fase 3.2 (10.240): "tiene pago" compone libro + caja chica + comisión de factoring — ver
  // src/utils/estadoCuenta/facturaRecibidaPagada.js (fuente única, compartida con el chip/filtro
  // de ConciliacionSII/index.js). Una NC (tipo_dte=61) no se "paga" — queda con el criterio viejo
  // (bug aparte, chico, fuera de esta pieza; ver medicion-asignar-recibida-saldada-2026-09-26.md).
  const facturaEstaPagada =
    factura && !esNotaCredito
      ? facturaRecibidaEstaPagada(factura, ctxPago)
      : factura?.estado_vinculacion === "vinculada";

  const [cuentaER, setCuentaER] = useState("");
  const [tipoContable, setTipoContable] = useState("");
  const [recordar, setRecordar] = useState(false);
  const [proveedor, setProveedor] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [plazopago, setPlazopago] = useState("");
  // Confirmación inline de "Ignorar factura" (molde confirmandoDesvincular, sin window.confirm).
  // Incidente real (2026-10-05): "Descartar" se confundía con cancelar y marcó 2 facturas como
  // ignoradas sin querer — ahora pide confirmación explícita y hay un "Cancelar" real al lado.
  const [confirmandoIgnorar, setConfirmandoIgnorar] = useState(false);

  // ─── NC (tipo_dte=61): vínculo manual al documento que anula/ajusta ───
  // Selector 100% manual (sin sugerencia automática de candidato). Ambos obligatorios para guardar
  // una NC; se puede dejar la NC sin guardar (queda 'pendiente') si aún no se sabe qué anula.
  const [docReferenciadoId, setDocReferenciadoId] = useState("");
  const [tipoRelacionNc, setTipoRelacionNc] = useState("");
  const [docsCandidatosNc, setDocsCandidatosNc] = useState([]);

  // Empleados activos del país — para el selector de destino "empleado" de las líneas de activo.
  const [empleadosDestino, setEmpleadosDestino] = useState([]);

  // Líneas de activo
  const [lineasActivo, setLineasActivo] = useState([]);
  const [lineasExpandidas, setLineasExpandidas] = useState({});
  const [edificios, setEdificios] = useState([]);
  const [proyectosCarteleria, setProyectosCarteleria] = useState([]);

  // Fase 2e (proyección/factura/pago, 2026-10-05) — freno anti-duplicado: antes de crear un activo
  // nuevo para esta factura, ofrecer vincularla a una proyección (planificado/ejecutado_sin_factura)
  // ya existente del mismo proveedor. `eligioCrearNuevo` recuerda que el usuario descartó el freno
  // explícitamente (10.4, nunca se auto-selecciona ni se salta solo).
  const [activosCandidatosProyeccion, setActivosCandidatosProyeccion] = useState([]);
  const [cargandoCandidatosProyeccion, setCargandoCandidatosProyeccion] = useState(false);
  const [eligioCrearNuevo, setEligioCrearNuevo] = useState(false);
  const [activosSeleccionadosProyeccion, setActivosSeleccionadosProyeccion] = useState(() => new Set());
  const [vinculandoProyeccion, setVinculandoProyeccion] = useState(false);

  // Activos ya creados para esta factura (vista de solo-lectura cuando está vinculada).
  const [activosVinculados, setActivosVinculados] = useState([]);
  const [desvinculando, setDesvinculando] = useState(false);
  // Confirmación inline del Desvincular (en vez de window.confirm, que en algunos navegadores
  // queda suprimido y hacía que el botón "no hiciera nada").
  const [confirmandoDesvincular, setConfirmandoDesvincular] = useState(false);
  // Se acaba de desvincular la clasificación dentro de esta sesión del modal: fuerza el
  // modo editable aunque el prop `factura` (stale) siga diciendo tipo_contable != null.
  const [desvinculada, setDesvinculada] = useState(false);
  // Última factura por la que se inicializó el formulario. Evita que un re-render del padre
  // que re-pase el mismo objeto `factura` (otra referencia, misma id) re-dispare el efecto de
  // apertura y pise el estado local (p.ej. desvinculada=false) tras desvincular.
  const facturaInicializadaId = useRef(null);

  // Cargar proveedor (por rut_ruc) + contexto de selectores.
  const cargarContexto = useCallback(async () => {
    if (!factura || !pais) return;
    setCargando(true);
    try {
      // Buscar proveedor por rut_ruc
      const { data: proveedores } = await client
        .from("proveedores")
        .select("id, nombre, rut_ruc, cuenta_er_default, tipo_contable_default")
        .eq("rut_ruc", factura.rut_emisor)
        .eq("pais", pais)
        .limit(1);

      const prov = proveedores?.[0] || null;
      setProveedor(prov);

      // Pre-seleccionar defaults del proveedor SOLO si la factura aún no tiene valores propios
      // (modo Editar: respetar lo que ya quedó persistido en facturas_sii_recibidas).
      if (prov?.cuenta_er_default && !factura.cuenta_er) {
        setCuentaER(prov.cuenta_er_default);
      }
      if (prov?.tipo_contable_default && !factura.tipo_contable) {
        setTipoContable(prov.tipo_contable_default);
      }

      // Cargar edificios, proyectos cartelería y empleados activos para los selectores de
      // destino de las líneas de activo.
      const [{ data: edifData }, { data: cartData }, { data: empData }] = await Promise.all([
        client.from("edificios").select("id, edificio").eq("pais", pais).order("edificio"),
        client
          .from("proyectos_carteleria")
          .select("id, nombre_proyecto, referencia_id")
          .eq("pais", pais)
          .order("nombre_proyecto"),
        client
          .from("empleados")
          .select("id, usuario_id, nombre, apellido, egreso_fecha")
          .eq("pais", pais)
          .is("egreso_fecha", null)
          .order("nombre"),
      ]);
      setEdificios(edifData || []);
      setProyectosCarteleria(cartData || []);
      setEmpleadosDestino(empData || []);

      // Activos ya vinculados a esta factura (C3 — vista de vínculo actual, espejo de C1). Se trae
      // fecha_compra para mostrar la fecha de compra (y saber si deprecia) en la card.
      const { data: activosData } = await client
        .from("activos")
        .select(
          "id, tipo_activo, descripcion, cantidad, costo_unitario, fecha_compra, destino_tipo, destino_id, destino_empleado_id"
        )
        .eq("factura_sii_recibida_id", factura.id)
        .eq("eliminado", false);
      setActivosVinculados(activosData || []);

      // NC (tipo_dte=61): documentos candidatos que puede anular/ajustar. Lista plana para elegir
      // manualmente — MISMO rut_emisor y MISMO país que la NC, excluyendo la propia NC y otras NC
      // (sólo facturas). Orden por fecha_emision; sin preselección ni resaltado por monto.
      if (esNotaCreditoDte(factura) && factura.rut_emisor) {
        let q = client
          .from("facturas_sii_recibidas")
          .select("id, folio, tipo_dte, fecha_emision, monto_total")
          .eq("rut_emisor", factura.rut_emisor)
          .neq("id", factura.id)
          .neq("tipo_dte", 61)
          .order("fecha_emision", { ascending: true });
        if (factura.pais) q = q.eq("pais", factura.pais);
        const { data: candidatos } = await q;
        setDocsCandidatosNc(candidatos || []);
      }
    } catch (err) {
      console.error("Error cargando contexto:", err);
    } finally {
      setCargando(false);
    }
  }, [factura, pais]);

  useEffect(() => {
    if (!isOpen) {
      // Al cerrar, permitir que la próxima apertura re-inicialice (incluso la misma factura).
      facturaInicializadaId.current = null;
      return;
    }
    if (!factura) return;
    // Inicializar SOLO al abrir o al cambiar de factura (por id). Si el padre re-pasa el mismo
    // objeto con otra referencia, no re-inicializamos: así no se pisa el estado tras desvincular.
    if (factura.id === facturaInicializadaId.current) return;
    facturaInicializadaId.current = factura.id;

    setCuentaER(factura.cuenta_er || "");
    setTipoContable(factura.tipo_contable || "");
    setRecordar(false);
    setConfirmando(false);
    setModoEdicion(false);
    setProveedor(null);
    setLineasActivo([]);
    setActivosVinculados([]);
    setDesvinculada(false);
    setConfirmandoDesvincular(false);
    setConfirmandoIgnorar(false);
    setPlazopago(factura.plazopago || "");
    setEligioCrearNuevo(false);
    setActivosSeleccionadosProyeccion(new Set());
    // NC: precargar el vínculo ya persistido (modo edición); si no, vacío.
    setDocReferenciadoId(factura.documento_referenciado_id ? String(factura.documento_referenciado_id) : "");
    setTipoRelacionNc(factura.tipo_relacion_nc || "");
    setDocsCandidatosNc([]);
    cargarContexto();
  }, [isOpen, factura, cargarContexto]);

  // Fase 2e: al clasificar como Activo, buscar proyecciones candidatas de este proveedor ANTES de
  // dejar crear un activo nuevo. Mismo criterio que `VincularProyeccionFacturaModal` (molde): si
  // falla la consulta, no se bloquea el flujo actual (10.177 — se trata como "0 candidatos", el
  // camino de siempre sigue disponible).
  useEffect(() => {
    if (!isOpen || tipoContable !== "activo" || !pais) {
      setActivosCandidatosProyeccion([]);
      return;
    }
    const proveedorId = proveedor?.id || factura?.proveedor_id || null;
    let vivo = true;
    (async () => {
      setCargandoCandidatosProyeccion(true);
      try {
        const candidatos = await getActivosParaVincularFactura({ pais, proveedorId });
        if (vivo) setActivosCandidatosProyeccion(candidatos);
      } catch (err) {
        console.error("Error consultando activos candidatos de proyección:", err);
        if (vivo) setActivosCandidatosProyeccion([]);
      } finally {
        if (vivo) setCargandoCandidatosProyeccion(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [isOpen, tipoContable, pais, proveedor, factura]);

  const toggleActivoProyeccion = (id) => {
    setActivosSeleccionadosProyeccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Vincular directo a 1+ proyecciones existentes — camino completo y autónomo (no pasa por
  // `crearActivo` ni por el panel de confirmación de abajo, que asume líneas nuevas). Mismo
  // candado de "Pago estimado" que el camino de crear (si la factura todavía no tiene pago real).
  const handleVincularProyeccionExistente = async () => {
    if (activosSeleccionadosProyeccion.size === 0) return;
    if (!facturaEstaPagada && !plazopago) {
      toast.error("Ingresá una fecha de pago estimada para continuar");
      return;
    }
    setVinculandoProyeccion(true);
    try {
      await vincularFacturaRecibida(factura.id, {
        proveedor_id: proveedor?.id || factura.proveedor_id || null,
        cuenta_er: null,
        cuenta_er_categoria: null,
        tipo_contable: "activo",
        usuario_id: userId,
      });
      if (!facturaEstaPagada) {
        const resultado = await guardarFechaFactura({
          factura,
          tipo: "pago",
          nuevaFecha: plazopago,
          modo: MODOS_VALIDACION.DATO_DOCUMENTO,
        });
        if (resultado.error) throw new Error(resultado.error);
      }
      const activoIds = [...activosSeleccionadosProyeccion];
      await vincularFacturaActivo({
        facturaId: factura.id,
        activoIds,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
      });
      toast.success(
        activoIds.length > 1
          ? `${activoIds.length} activos vinculados a la proyección`
          : "Vinculada a la proyección existente"
      );
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error vinculando a la proyección:", err);
      toast.error("Error al vincular: " + (err.message || err));
    } finally {
      setVinculandoProyeccion(false);
    }
  };

  // Derivar categoría ER del valor seleccionado
  const getCategoriaER = (valor) => {
    const cuenta = CUENTAS_ER.find((c) => c.value === valor);
    if (!cuenta) return null;
    if (cuenta.grupo === "Costos Fijos") return "costo_fijo";
    if (cuenta.grupo === "Costos Variables") return "costo_variable";
    return "otro";
  };

  // ─── Helpers líneas de activo ──────────────────────────

  const agregarLinea = () => setLineasActivo([...lineasActivo, { ...LINEA_VACIA }]);

  const eliminarLinea = (idx) => setLineasActivo(lineasActivo.filter((_, i) => i !== idx));

  const actualizarLinea = (idx, campo, valor) => {
    setLineasActivo((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        const updated = { ...l, [campo]: valor };
        // Al cambiar destino_tipo, resetear ambos ids de destino (integer y uuid empleado)
        if (campo === "destino_tipo") {
          updated.destino_id = "";
          updated.destino_empleado_id = "";
        }
        return updated;
      })
    );
  };

  // ─── Totalizador ──────────────────────────────────────

  const totalLineas = lineasActivo.reduce(
    (sum, l) => sum + (Number(l.cantidad) || 0) * (Number(l.costo_unitario) || 0),
    0
  );
  const montoNeto = Number(factura?.monto_neto) || 0;
  const diferenciaOk = lineasActivo.length > 0 && Math.abs(totalLineas - montoNeto) < 1;

  // ─── Validación líneas ────────────────────────────────

  const validarLineas = () => {
    if (lineasActivo.length === 0) {
      toast.error("Agregá al menos una línea de activo");
      return false;
    }
    for (let i = 0; i < lineasActivo.length; i++) {
      const l = lineasActivo[i];
      if (!l.tipo_activo) {
        toast.error(`Línea ${i + 1}: seleccioná un tipo de activo`);
        return false;
      }
      if (!l.cantidad || l.cantidad < 1) {
        toast.error(`Línea ${i + 1}: cantidad debe ser al menos 1`);
        return false;
      }
      if (!l.costo_unitario || l.costo_unitario <= 0) {
        toast.error(`Línea ${i + 1}: costo unitario debe ser mayor a 0`);
        return false;
      }
    }
    return true;
  };

  // ─── Asignar ──────────────────────────────────────────

  const handleAsignar = async () => {
    // Si la factura ya tiene pago (compuesto — ver facturaEstaPagada arriba), este modal solo
    // agrega la clasificación contable: no se pide ni se persiste "Pago estimado".
    const tienePago = facturaEstaPagada;

    // NC (tipo_dte=61): ambos campos del vínculo son obligatorios para guardar. Si aún no se sabe
    // qué anula, no se guarda (la NC queda 'pendiente') — no se fuerza una vinculación a ciegas.
    if (esNotaCredito && (!docReferenciadoId || !tipoRelacionNc)) {
      toast.error("Para una nota de crédito, elegí el documento que anula/ajusta y el tipo de relación");
      return;
    }

    // Una NC nunca puede clasificarse como Activo (mismo guard que ClasificarModal, regla 10.177).
    if (esNotaCredito && tipoContable === "activo") {
      toast.error("Una nota de crédito no puede clasificarse como Activo. Elegí Gasto.");
      return;
    }

    if (tipoContable === "gasto" && !cuentaER) {
      toast.error("Seleccioná una cuenta del ER");
      return;
    }
    if (tipoContable === "activo" && !validarLineas()) return;
    if (!tienePago && !plazopago) {
      toast.error("Ingresá una fecha de pago estimada para continuar");
      return;
    }

    const moneda = pais === "Chile" ? "CLP" : "PEN";

    setGuardando(true);
    try {
      // 1. Vincular la factura SII recibida. Activo → cuenta_er NULL (criterio canónico,
      // unifica la asimetría histórica de '' con honorarios; ver migración 20260720). En
      // activo el <select> de cuenta no se muestra y `cuentaER` queda '', que se normaliza acá.
      const cuentaErFinal = tipoContable === "activo" ? null : cuentaER;
      await vincularFacturaRecibida(factura.id, {
        proveedor_id: proveedor?.id || factura.proveedor_id || null,
        cuenta_er: cuentaErFinal,
        cuenta_er_categoria: getCategoriaER(cuentaErFinal),
        tipo_contable: tipoContable,
        usuario_id: userId,
      });

      // Persistir plazopago solo si la factura aún no tiene pago (no vinculada). B.3 Commit 12
      // (2026-09-27, camino #12): el UPDATE crudo pasa por el writer único guardarFechaFactura,
      // modo DATO_DOCUMENTO (sigue admitiendo cualquier fecha, incluso pasada — la factura puede
      // haber llegado tarde). El gate `!tienePago` de arriba sigue siendo el filtro previo (decide
      // SI se intenta guardar); ya no redacta su propio mensaje de fecha — el único mensaje de
      // fecha en todo el sistema es el que devuelve el validador (10.1/§11 decisión 4 del diseño).
      if (!tienePago) {
        const resultado = await guardarFechaFactura({
          factura,
          tipo: "pago",
          nuevaFecha: plazopago,
          modo: MODOS_VALIDACION.DATO_DOCUMENTO,
        });
        if (resultado.error) throw new Error(resultado.error);
      }

      // NC (tipo_dte=61): persistir el vínculo al documento que anula/ajusta + auditoría (usuario
      // real logueado, regla 10.104). Se compara/guarda por ID de documento (regla 10.7).
      if (esNotaCredito) {
        await client
          .from("facturas_sii_recibidas")
          .update({
            documento_referenciado_id: Number(docReferenciadoId),
            tipo_relacion_nc: tipoRelacionNc,
            nc_vinculado_por: userId,
            nc_vinculado_at: new Date().toISOString(),
          })
          .eq("id", factura.id);
      }

      // 2. Si es activo, crear activos
      if (tipoContable === "activo" && lineasActivo.length > 0) {
        for (const l of lineasActivo) {
          // eslint-disable-next-line no-await-in-loop
          await crearActivo({
            pais,
            fecha_compra: factura.fecha_emision?.substring(0, 10) || new Date().toISOString().substring(0, 10),
            tipo_activo: l.tipo_activo,
            descripcion: l.descripcion || factura.razon_social_emisor,
            numero_serie: l.numero_serie || null,
            numero_factura: String(factura.folio),
            cantidad: Number(l.cantidad) || 1,
            costo_unitario: Number(l.costo_unitario) || 0,
            moneda,
            vida_util_meses: getVidaUtilPorTipo(l.tipo_activo),
            valor_residual: 0,
            estado: "activo",
            destino_tipo: l.destino_tipo || null,
            // Empleado → destino_empleado_id (uuid); edificio/cartelería → destino_id (integer).
            destino_id: l.destino_tipo === "empleado" ? null : l.destino_id || null,
            destino_empleado_id: l.destino_tipo === "empleado" ? l.destino_empleado_id || null : null,
            proveedor_id: proveedor?.id || null,
            factura_sii_recibida_id: factura.id,
          });
        }
      }

      // 3. Recordar cuenta_er_default para el proveedor
      if (recordar && proveedor) {
        await actualizarProveedor(proveedor.id, {
          cuenta_er_default: cuentaER,
          tipo_contable_default: tipoContable,
        });
      }

      toast.success("Factura asignada correctamente");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error asignando:", err);
      toast.error(err.message || "Error al asignar factura");
    } finally {
      setGuardando(false);
    }
  };

  const handleIgnorar = async () => {
    setGuardando(true);
    try {
      await ignorarFacturaRecibida(factura.id, userId);
      toast.success("DTE marcado como ignorado");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error ignorando:", err);
      toast.error("Error al ignorar DTE");
    } finally {
      setGuardando(false);
    }
  };

  const handleReactivar = async () => {
    setGuardando(true);
    try {
      await reactivarFactura(factura.id, "recibida");
      toast.success("Factura reactivada");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error reactivando:", err);
      toast.error("Error al reactivar factura");
    } finally {
      setGuardando(false);
    }
  };

  // Desvincular SOLO la clasificación contable de la factura (no toca el pago bancario:
  // MBF / movimientos_caja / movimientos_bancarios se manejan desde Flujo de Caja).
  // - Limpia tipo_contable/cuenta_er/cuenta_er_categoria + vinculado_* y soft-elimina los
  //   activos creados para esta factura (+ borra sus filas en factura_activo_lineas) para que
  //   re-clasificarla no duplique activos.
  // - estado_vinculacion: si la factura tiene pago bancario (fila en movimiento_bancario_facturas)
  //   queda 'vinculada' (el pago sigue vivo); si no, vuelve a 'pendiente'.
  const handleDesvincular = async () => {
    setDesvinculando(true);
    try {
      // 1. ¿Tiene pago bancario? Determina el estado_vinculacion final. Best-effort: si la
      //    consulta falla, conservamos 'vinculada' (no perder el pago).
      let tienePagoBancario = true;
      try {
        const { data: mbfRows, error: errMbf } = await client
          .from("movimiento_bancario_facturas")
          .select("id")
          .eq("factura_tipo", "sii_recibida")
          .eq("factura_id", factura.id);
        if (errMbf) throw errMbf;
        tienePagoBancario = (mbfRows || []).length > 0;
      } catch (err) {
        console.error("desvincular: error consultando MBF (se conserva vinculada):", err);
      }

      // 2. Reset de la clasificación contable en la fila SII. PASO CRÍTICO: si esto falla,
      //    abortamos (no tocamos la UI). Si funciona, la factura ya quedó sin clasificación.
      const { error: errSii } = await client
        .from("facturas_sii_recibidas")
        .update({
          tipo_contable: null,
          cuenta_er: null,
          cuenta_er_categoria: null,
          vinculado_por: null,
          vinculado_at: null,
          estado_vinculacion: tienePagoBancario ? "vinculada" : "pendiente",
        })
        .eq("id", factura.id);
      if (errSii) throw errSii;

      // 3. Refrescar el estado local INMEDIATAMENTE → el re-render muestra el formulario
      //    editable. No cerramos el modal. Se hace ANTES de la limpieza de activos para que un
      //    fallo en esa limpieza no impida pasar a modo editable.
      setDesvinculada(true);
      setTipoContable("");
      setCuentaER("");
      setLineasActivo([]);
      setActivosVinculados([]);
      setConfirmando(false);
      setConfirmandoDesvincular(false);
      toast.success("Clasificación desvinculada. Ya podés reclasificar.");

      // 4. Limpieza best-effort (no bloquea la UI): soft-delete de activos + borrar líneas.
      try {
        const { data: activosFactura } = await client
          .from("activos")
          .select("id")
          .eq("factura_sii_recibida_id", factura.id)
          .eq("eliminado", false);
        for (const a of activosFactura || []) {
          // eslint-disable-next-line no-await-in-loop
          await eliminarActivo(a.id);
        }
      } catch (err) {
        console.error("desvincular: error soft-deleting activos:", err);
      }
      try {
        await client.from("factura_activo_lineas").delete().eq("factura_sii_recibida_id", factura.id);
      } catch (err) {
        console.error("desvincular: error borrando factura_activo_lineas:", err);
      }
    } catch (err) {
      console.error("Error desvinculando:", err);
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setDesvinculando(false);
    }
  };

  // C3 — Desvincular UN activo (espejo de C1 / ClasificarHonorarioModal): el activo vuelve a
  // 'planificado', se limpia fecha_compra + la FK → deja de depreciar (getDepreciacionMensual exige
  // estado 'activo' + fecha_compra). NO borra el activo ni resetea la clasificación de la factura (a
  // diferencia de "Desvincular clasificación"). Reusa actualizarActivo (10.1). Optimista: saca la card
  // sin cerrar el modal + borra su línea de factura_activo_lineas para no dejar metadato huérfano.
  const handleDesvincularActivo = async (activo) => {
    if (!activo) return;
    setDesvinculando(true);
    try {
      await actualizarActivo(activo.id, {
        estado: "planificado",
        fecha_compra: null,
        factura_sii_recibida_id: null,
      });
      try {
        await client.from("factura_activo_lineas").delete().eq("activo_id", activo.id);
      } catch (err) {
        console.error("desvincular activo: error borrando factura_activo_lineas:", err);
      }
      setActivosVinculados((prev) => prev.filter((a) => a.id !== activo.id));
      toast.success(`Activo #${activo.id} desvinculado (vuelve a planificado)`);
    } catch (err) {
      console.error("Error desvinculando activo:", err);
      toast.error("Error al desvincular el activo: " + (err.message || ""));
    } finally {
      setDesvinculando(false);
    }
  };

  if (!factura) return null;

  const esIgnorada = factura.estado_vinculacion === "ignorada";
  // Factura ya pagada (criterio compuesto — ver facturaEstaPagada arriba): el pago ya existe, así
  // que el modal no pide "Pago estimado" ni lo persiste al clasificar.
  const esVinculada = facturaEstaPagada;
  // Modo solo-lectura: solo cuando la factura está vinculada Y ya tiene clasificación contable
  // (y no se acaba de desvincular en esta sesión). Una vinculada SIN clasificar (tipo_contable
  // null) muestra el formulario editable para poder clasificarla.
  const esVinculadaConClasificacion = esVinculada && !!factura.tipo_contable && !desvinculada;
  const cuentaLabel = CUENTAS_ER.find((c) => c.value === cuentaER)?.label;
  // Modo B (rápido) solo aplica al asignar una factura pendiente. En modo Editar
  // (factura.cuenta_er ya seteado) se salta a Modo C para que el usuario vea y edite
  // los valores reales persistidos en la fila.
  const tieneDefaults = proveedor?.cuenta_er_default && proveedor?.tipo_contable_default && !factura.cuenta_er;
  // La NC nunca entra al modo rápido: siempre muestra el form completo para exponer sus 2 campos.
  const modoRapido = tieneDefaults && !modoEdicion && !esVinculada && !esNotaCredito;
  const defaultCuentaLabel =
    CUENTAS_ER.find((c) => c.value === proveedor?.cuenta_er_default)?.label || proveedor?.cuenta_er_default;

  // ─── Opciones destino por línea ───────────────────────

  const getDestinoOpciones = (destTipo) => {
    if (destTipo === "edificio") return edificios.map((e) => ({ value: e.id, label: e.edificio }));
    if (destTipo === "carteleria") {
      return proyectosCarteleria.map((p) => ({ value: p.id, label: p.nombre_proyecto || p.referencia_id }));
    }
    if (destTipo === "empleado") {
      return empleadosDestino.map((emp) => ({ value: emp.id, label: `${emp.nombre} ${emp.apellido || ""}`.trim() }));
    }
    return [];
  };

  const esFacturaPendiente = !facturaEstaPagada && factura.estado_vinculacion === "pendiente";

  // Fase 2e: mostrar el freno anti-duplicado en vez del editor de líneas mientras haya candidatos
  // y el usuario no haya elegido explícitamente "Crear activo nuevo".
  const mostrarGateProyeccion = tipoContable === "activo" && !eligioCrearNuevo;
  const mostrarCandidatosProyeccion =
    mostrarGateProyeccion && !cargandoCandidatosProyeccion && activosCandidatosProyeccion.length > 0;
  const mostrarEditorLineasActivo =
    tipoContable === "activo" &&
    !cargandoCandidatosProyeccion &&
    (eligioCrearNuevo || activosCandidatosProyeccion.length === 0);
  // Título del freno: distingue si hay candidatos del MISMO proveedor o solo "sin proveedor
  // asignado" (presupuestos genéricos) — mismo criterio de agrupación que `ActivosCandidatosLista`.
  const tieneCandidatosDelProveedor = activosCandidatosProyeccion.some((a) => a.proveedor_id != null);
  const tituloGateProyeccion = tieneCandidatosDelProveedor
    ? "Este proveedor tiene proyecciones sin factura"
    : "Hay proyecciones sin proveedor asignado que podrían corresponder a esta factura";

  // Footer compartido por los 3 estados que antes mostraban "Descartar" (confundible con cancelar
  // — incidente real 2026-10-05). Con `confirmandoIgnorar=false`: "Cancelar" real (cierra sin
  // escribir nada) + "Ignorar factura" (abre la confirmación) + el botón primario de ese estado
  // (o ninguno, en modoRapido). Con `confirmandoIgnorar=true`: solo la barra de confirmación,
  // molde `confirmandoDesvincular` — nunca auto-ignora, pide "Sí, ignorar" explícito.
  const renderFooterConIgnorar = (botonPrimario = null) =>
    confirmandoIgnorar ? (
      <>
        <span style={{ fontSize: "0.78rem", color: "#856404", marginRight: "auto" }}>
          ¿Ignorar esta factura? Sale de pendientes. Se puede reactivar.
        </span>
        <Button color="secondary" outline size="sm" onClick={() => setConfirmandoIgnorar(false)} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="warning" size="sm" onClick={handleIgnorar} disabled={guardando}>
          {guardando ? "Ignorando..." : "Sí, ignorar"}
        </Button>
      </>
    ) : (
      <>
        <Button color="secondary" outline size="sm" onClick={toggle} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="secondary" outline size="sm" onClick={() => setConfirmandoIgnorar(true)} disabled={guardando}>
          Ignorar factura
        </Button>
        {botonPrimario}
      </>
    );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {esVinculadaConClasificacion ? "DTE vinculado" : esFacturaPendiente ? "Asignar DTE" : "Editar DTE"} — Folio{" "}
        {factura.folio} &middot; {factura.razon_social_emisor}
      </ModalHeader>
      <ModalBody>
        {/* Banner ignorada */}
        {esIgnorada && (
          <div
            style={{
              background: "#fff3cd",
              color: "#856404",
              padding: "10px 14px",
              borderRadius: 6,
              marginBottom: 14,
              fontSize: "0.82rem",
              fontWeight: 600,
            }}
          >
            Esta factura está marcada como descartada
          </div>
        )}

        {/* Detalle del DTE — una línea compacta */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.8rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
            alignItems: "center",
          }}
        >
          <span>
            <strong>Fecha:</strong> {formatFecha(factura.fecha_emision)}
          </span>
          <span>
            <strong>RUT:</strong> {formatRut(factura.rut_emisor)}
          </span>
          <span>
            <strong>Neto:</strong> {formatMonto(factura.monto_neto)}
          </span>
          <span>
            <strong>IVA:</strong> {formatMonto(factura.monto_iva)}
          </span>
          <span>
            <strong>Total:</strong> {formatMonto(factura.monto_total)}
          </span>
        </div>

        {!esIgnorada && cargando && (
          <div style={{ textAlign: "center", padding: 16, color: "#999" }}>Cargando datos del proveedor...</div>
        )}

        {/* Modo rápido: proveedor con cuenta recordada */}
        {!esIgnorada && !cargando && modoRapido && (
          <div
            style={{
              padding: "14px 16px",
              background: "#eafaf1",
              borderRadius: 6,
              border: "1px solid #a9dfbf",
              fontSize: "0.85rem",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <strong>{proveedor.nombre}</strong>
              {" se asigna como "}
              <strong style={{ textTransform: "capitalize" }}>{proveedor.tipo_contable_default}</strong>
              {" en "}
              <strong>{defaultCuentaLabel}</strong>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                Pago estimado <span style={{ color: "#e74c3c" }}>*</span>
              </label>
              <SelectorFechaDocumento
                value={plazopago}
                onChange={(nuevaFecha) => setPlazopago(nuevaFecha || "")}
                pais={pais}
                modo={MODOS_VALIDACION.DATO_DOCUMENTO}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: `1px solid ${!plazopago ? "#e74c3c" : "#aed6f1"}`,
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button color="success" size="sm" style={{ minWidth: 100 }} onClick={handleAsignar} disabled={guardando}>
                {guardando ? "Guardando..." : "Confirmar"}
              </Button>
              <Button color="warning" size="sm" style={{ minWidth: 100 }} onClick={() => setModoEdicion(true)}>
                Cambiar
              </Button>
              <Button
                color="danger"
                size="sm"
                style={{ minWidth: 100 }}
                onClick={async () => {
                  await actualizarProveedor(proveedor.id, {
                    cuenta_er_default: null,
                    tipo_contable_default: null,
                  });
                  toast.success(`Asociación olvidada para ${proveedor.nombre}`);
                  setProveedor({ ...proveedor, cuenta_er_default: null, tipo_contable_default: null });
                  setModoEdicion(true);
                }}
              >
                Olvidar
              </Button>
            </div>
          </div>
        )}

        {!esIgnorada && !cargando && !modoRapido && !esVinculadaConClasificacion && (
          <>
            {/* ─── NC (tipo_dte=61): vínculo manual al documento que anula/ajusta ─── */}
            {esNotaCredito && (
              <div
                style={{
                  border: "1px solid #f5c6cb",
                  background: "#fdf3f4",
                  borderRadius: 6,
                  padding: "12px 14px",
                  marginBottom: 14,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#a94442", marginBottom: 10 }}>
                  Nota de crédito — vínculo al documento original
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Documento que anula o ajusta esta NC <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <select
                    value={docReferenciadoId}
                    onChange={(e) => setDocReferenciadoId(e.target.value)}
                    style={{
                      width: "100%",
                      height: 36,
                      fontSize: "0.82rem",
                      padding: "4px 10px",
                      border: `1px solid ${!docReferenciadoId ? "#e74c3c" : "#ced4da"}`,
                      borderRadius: 4,
                    }}
                  >
                    <option value="">— Seleccionar documento —</option>
                    {docsCandidatosNc.map((d) => (
                      <option key={d.id} value={d.id}>
                        {`Folio ${d.folio} · ${formatFecha(d.fecha_emision)} · ${formatMonto(d.monto_total)}`}
                      </option>
                    ))}
                  </select>
                  {docsCandidatosNc.length === 0 && (
                    <div style={{ fontSize: "0.72rem", color: "#999", marginTop: 4 }}>
                      No hay facturas del mismo emisor y país para vincular.
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Tipo de relación <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <select
                    value={tipoRelacionNc}
                    onChange={(e) => setTipoRelacionNc(e.target.value)}
                    style={{
                      width: "100%",
                      height: 36,
                      fontSize: "0.82rem",
                      padding: "4px 10px",
                      border: `1px solid ${!tipoRelacionNc ? "#e74c3c" : "#ced4da"}`,
                      borderRadius: 4,
                    }}
                  >
                    <option value="">— Seleccionar tipo —</option>
                    <option value="anula_error">Anula factura — error, se reemitió</option>
                    <option value="ajuste_comercial">Ajuste comercial — descuento, devolución real</option>
                  </select>
                </div>
              </div>
            )}

            {/* Proveedor identificado */}
            {proveedor && (
              <div
                style={{
                  fontSize: "0.78rem",
                  marginBottom: 12,
                  padding: "8px 12px",
                  background: "#d5f5e3",
                  borderRadius: 4,
                  color: "#27ae60",
                }}
              >
                Proveedor identificado: <strong>{proveedor.nombre}</strong> (ID {proveedor.id})
                {proveedor.cuenta_er_default && (
                  <span>
                    {" "}
                    — Cuenta habitual:{" "}
                    <strong>
                      {CUENTAS_ER.find((c) => c.value === proveedor.cuenta_er_default)?.label ||
                        proveedor.cuenta_er_default}
                    </strong>
                  </span>
                )}
              </div>
            )}

            {/* Selector Tipo Contable (arriba del todo) */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Tipo contable
              </label>
              <div style={{ display: "flex", gap: 16, fontSize: "0.82rem" }}>
                <label style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="tipoContable"
                    value="gasto"
                    checked={tipoContable === "gasto"}
                    onChange={() => {
                      setTipoContable("gasto");
                      setLineasActivo([]);
                    }}
                    style={{ marginRight: 4 }}
                  />
                  Gasto
                </label>
                <label style={{ cursor: esNotaCredito ? "not-allowed" : "pointer" }}>
                  <input
                    type="radio"
                    name="tipoContable"
                    value="activo"
                    checked={tipoContable === "activo"}
                    disabled={esNotaCredito}
                    onChange={() => {
                      setTipoContable("activo");
                      setEligioCrearNuevo(false);
                      setActivosSeleccionadosProyeccion(new Set());
                      if (lineasActivo.length === 0) agregarLinea();
                    }}
                    style={{ marginRight: 4 }}
                  />
                  Activo{esNotaCredito ? " (no aplica a NC)" : ""}
                </label>
              </div>
            </div>

            {/* Selector Cuenta ER — solo para gastos */}
            {tipoContable === "gasto" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Cuenta del Estado de Resultados
                </label>
                <select
                  value={cuentaER}
                  onChange={(e) => setCuentaER(e.target.value)}
                  style={{
                    width: "100%",
                    height: 36,
                    fontSize: "0.82rem",
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="">— Seleccionar cuenta —</option>
                  {Object.entries(CUENTAS_POR_GRUPO).map(([grupo, cuentas]) => (
                    <optgroup key={grupo} label={grupo}>
                      {cuentas.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {/* ─── Fase 2e: freno anti-duplicado — proyecciones candidatas de este proveedor ─── */}
            {mostrarGateProyeccion && cargandoCandidatosProyeccion && (
              <div style={{ textAlign: "center", padding: 16, color: "#999", fontSize: "0.82rem" }}>
                Buscando proyecciones de este proveedor…
              </div>
            )}
            {mostrarCandidatosProyeccion && (
              <div
                style={{
                  border: "1px solid #aed6f1",
                  background: "#eaf2f8",
                  borderRadius: 6,
                  padding: "12px 14px",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#2874a6", marginBottom: 8 }}>
                  {tituloGateProyeccion}
                </div>
                <ActivosCandidatosLista
                  activos={activosCandidatosProyeccion}
                  seleccionados={activosSeleccionadosProyeccion}
                  onToggle={toggleActivoProyeccion}
                />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={() => setEligioCrearNuevo(true)}
                    disabled={vinculandoProyeccion}
                  >
                    Crear activo nuevo
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleVincularProyeccionExistente}
                    disabled={vinculandoProyeccion || activosSeleccionadosProyeccion.size === 0}
                  >
                    {vinculandoProyeccion ? "Vinculando…" : "Vincular a la proyección"}
                  </Button>
                </div>
              </div>
            )}

            {/* ─── Sección Líneas de Activo (compacta) ──────── */}
            {mostrarEditorLineasActivo && (
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "10px 12px",
                  marginBottom: 12,
                  background: "#fcfcfd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>Líneas de activo ({lineasActivo.length})</span>
                  <Button color="dark" outline size="sm" onClick={agregarLinea} style={{ fontSize: "0.75rem" }}>
                    + Agregar línea
                  </Button>
                </div>

                {/* Cabecera columnas */}
                {lineasActivo.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      fontSize: "0.68rem",
                      color: "#888",
                      fontWeight: 600,
                      padding: "0 2px 4px",
                    }}
                  >
                    <span style={{ width: 200, minWidth: 200 }}>Tipo *</span>
                    <span style={{ width: 70, minWidth: 70, textAlign: "center" }}>Cant. *</span>
                    <span style={{ width: 120, minWidth: 120 }}>Costo unit. *</span>
                    <span style={{ width: 180, minWidth: 180 }}>Destino</span>
                    <span style={{ width: 22 }} />
                  </div>
                )}

                {lineasActivo.map((linea, idx) => {
                  const destinoOpciones = getDestinoOpciones(linea.destino_tipo);
                  const expanded = !!lineasExpandidas[idx];
                  const vidaUtil = linea.tipo_activo ? getVidaUtilPorTipo(linea.tipo_activo) : null;

                  return (
                    <div key={idx} style={{ marginBottom: 4 }}>
                      {/* Fila principal compacta */}
                      <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.78rem" }}>
                        {/* Tipo activo */}
                        <select
                          value={linea.tipo_activo}
                          onChange={(e) => actualizarLinea(idx, "tipo_activo", e.target.value)}
                          style={{
                            width: 200,
                            minWidth: 200,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 4px",
                          }}
                        >
                          <option value="">— Tipo —</option>
                          {TIPOS_ACTIVO.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>

                        {/* Cantidad */}
                        <input
                          type="number"
                          min={1}
                          value={linea.cantidad}
                          onChange={(e) => actualizarLinea(idx, "cantidad", parseInt(e.target.value, 10) || 1)}
                          style={{
                            width: 70,
                            minWidth: 70,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 6px",
                            textAlign: "center",
                          }}
                        />

                        {/* Costo unitario */}
                        <input
                          type="number"
                          min={0}
                          value={linea.costo_unitario}
                          onChange={(e) => actualizarLinea(idx, "costo_unitario", parseFloat(e.target.value) || 0)}
                          style={{
                            width: 120,
                            minWidth: 120,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 6px",
                          }}
                        />

                        {/* Destino — selector combinado tipo + id */}
                        {!linea.destino_tipo || destinoOpciones.length === 0 ? (
                          <select
                            value={linea.destino_tipo}
                            onChange={(e) => actualizarLinea(idx, "destino_tipo", e.target.value)}
                            style={{
                              width: 180,
                              minWidth: 180,
                              height: 30,
                              fontSize: "0.76rem",
                              border: "1px solid #ced4da",
                              borderRadius: 3,
                              padding: "2px 4px",
                            }}
                          >
                            {DESTINO_TIPOS.map((d) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ width: 180, minWidth: 180, display: "flex", gap: 3 }}>
                            <select
                              value={linea.destino_tipo === "empleado" ? linea.destino_empleado_id : linea.destino_id}
                              onChange={(e) =>
                                linea.destino_tipo === "empleado"
                                  ? actualizarLinea(idx, "destino_empleado_id", e.target.value || "")
                                  : actualizarLinea(idx, "destino_id", Number(e.target.value) || "")
                              }
                              style={{
                                flex: 1,
                                height: 30,
                                fontSize: "0.76rem",
                                border: "1px solid #ced4da",
                                borderRadius: 3,
                                padding: "2px 4px",
                              }}
                            >
                              <option value="">
                                {linea.destino_tipo === "edificio"
                                  ? "Edificio..."
                                  : linea.destino_tipo === "empleado"
                                    ? "Empleado..."
                                    : "Proyecto..."}
                              </option>
                              {destinoOpciones.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => actualizarLinea(idx, "destino_tipo", "")}
                              title="Cambiar destino"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#999",
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                padding: "0 2px",
                              }}
                            >
                              &larr;
                            </button>
                          </div>
                        )}

                        {/* Botón eliminar */}
                        <button
                          onClick={() => eliminarLinea(idx)}
                          disabled={lineasActivo.length <= 1}
                          style={{
                            width: 22,
                            minWidth: 22,
                            background: "none",
                            border: "none",
                            color: lineasActivo.length > 1 ? "#e74c3c" : "#ccc",
                            cursor: lineasActivo.length > 1 ? "pointer" : "default",
                            fontSize: "1rem",
                            padding: 0,
                            lineHeight: 1,
                          }}
                          title="Eliminar línea"
                        >
                          &times;
                        </button>
                      </div>

                      {/* Link "ver más" + campos expandibles */}
                      <div style={{ paddingLeft: 2, marginTop: 2 }}>
                        <button
                          onClick={() => setLineasExpandidas((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#2980b9",
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            padding: 0,
                          }}
                        >
                          {expanded ? "ocultar" : "ver más"}
                          {vidaUtil && !expanded ? ` (${vidaUtil}m)` : ""}
                        </button>

                        {expanded && (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              marginTop: 4,
                              marginBottom: 2,
                              fontSize: "0.76rem",
                            }}
                          >
                            <div style={{ flex: 2 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>Descripción</label>
                              <input
                                type="text"
                                value={linea.descripcion}
                                onChange={(e) => actualizarLinea(idx, "descripcion", e.target.value)}
                                placeholder="Descripción del activo"
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #ced4da",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>N° Serie</label>
                              <input
                                type="text"
                                value={linea.numero_serie}
                                onChange={(e) => actualizarLinea(idx, "numero_serie", e.target.value)}
                                placeholder="Opcional"
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #ced4da",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                }}
                              />
                            </div>
                            <div style={{ width: 80 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>Vida útil</label>
                              <input
                                type="text"
                                readOnly
                                value={vidaUtil ? `${vidaUtil}m` : "—"}
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #e9ecef",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                  background: "#f5f5f5",
                                  color: "#888",
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Totalizador */}
                {lineasActivo.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: 4,
                      background: diferenciaOk ? "#d5f5e3" : "#fdecea",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      marginTop: 6,
                    }}
                  >
                    <span>Total: {formatMonto(totalLineas)}</span>
                    <span>Neto: {formatMonto(montoNeto)}</span>
                    <span style={{ color: diferenciaOk ? "#27ae60" : "#e74c3c" }}>
                      {diferenciaOk ? "Coincide" : `Dif: ${formatMonto(Math.abs(totalLineas - montoNeto))}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Recordar para proveedor */}
            {proveedor && (
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: "0.78rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={recordar}
                    onChange={(e) => setRecordar(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Recordar esta cuenta para <strong>{proveedor.nombre}</strong>
                  {cuentaLabel && <span> ({cuentaLabel})</span>}
                </label>
              </div>
            )}
          </>
        )}

        {/* Vista de solo-lectura para facturas ya vinculadas y clasificadas (cerradas) */}
        {!esIgnorada && !cargando && esVinculadaConClasificacion && (
          <>
            {proveedor && (
              <div
                style={{
                  fontSize: "0.78rem",
                  marginBottom: 12,
                  padding: "8px 12px",
                  background: "#d5f5e3",
                  borderRadius: 4,
                  color: "#27ae60",
                }}
              >
                Proveedor identificado: <strong>{proveedor.nombre}</strong> (ID {proveedor.id})
              </div>
            )}

            {/* Clasificación contable actual (no editable) */}
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 6,
                padding: "12px 14px",
                marginBottom: 12,
                background: "#fcfcfd",
                fontSize: "0.82rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px 28px",
                  marginBottom: activosVinculados.length > 0 ? 10 : 0,
                }}
              >
                <div>
                  <strong>Tipo contable:</strong>{" "}
                  <span style={{ textTransform: "capitalize" }}>{factura.tipo_contable || "—"}</span>
                </div>
                {factura.tipo_contable === "gasto" && (
                  <div>
                    <strong>Cuenta ER:</strong>{" "}
                    {CUENTAS_ER.find((c) => c.value === factura.cuenta_er)?.label || factura.cuenta_er || "—"}
                  </div>
                )}
              </div>

              {/* C3 — Activo(s) vinculado(s): card explícita por activo (espejo de C1), con Desvincular
                  per-activo (→ 'planificado', deja de depreciar). Si son varios (split), se listan todos. */}
              {activosVinculados.length > 0 && (
                <div style={{ fontSize: "0.78rem" }}>
                  <strong style={{ color: "#555" }}>
                    Activo{activosVinculados.length > 1 ? "s" : ""} vinculado{activosVinculados.length > 1 ? "s" : ""}:{" "}
                    {activosVinculados.length}
                  </strong>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                    {activosVinculados.map((a) => {
                      const tipoLabel = TIPOS_ACTIVO.find((t) => t.value === a.tipo_activo)?.label || a.tipo_activo;
                      const destinoLabel =
                        a.destino_tipo === "edificio"
                          ? edificios.find((e) => e.id === a.destino_id)?.edificio ||
                            (a.destino_id ? `Edificio #${a.destino_id}` : null)
                          : a.destino_tipo === "carteleria"
                            ? proyectosCarteleria.find((p) => p.id === a.destino_id)?.nombre_proyecto || "Cartelería"
                            : a.destino_tipo === "empleado"
                              ? getDestinoOpciones("empleado").find((o) => o.value === a.destino_empleado_id)?.label ||
                                "Empleado"
                              : null;
                      const monto = Number(a.costo_unitario || 0) * (Number(a.cantidad) || 1);
                      return (
                        <div
                          key={a.id}
                          style={{
                            background: "#eafaf1",
                            border: "1px solid #a9dfbf",
                            borderRadius: 6,
                            padding: "8px 10px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: "#1e8449" }}>Vinculada al activo #{a.id}</div>
                            <div style={{ color: "#555" }}>
                              {[
                                destinoLabel || "Sin edificio",
                                `${a.cantidad || 1}× ${tipoLabel}`,
                                formatMonto(monto),
                                a.fecha_compra ? `compra ${a.fecha_compra}` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDesvincularActivo(a)}
                            disabled={desvinculando}
                            style={{
                              background: "none",
                              border: "1px solid #c0392b",
                              borderRadius: 4,
                              padding: "4px 12px",
                              fontSize: "0.76rem",
                              color: "#c0392b",
                              cursor: desvinculando ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {desvinculando ? "…" : "Desvincular"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Mensaje de factura cerrada */}
            <div
              style={{
                background: "#fff3cd",
                color: "#856404",
                padding: "10px 14px",
                borderRadius: 6,
                fontSize: "0.82rem",
                fontWeight: 600,
              }}
            >
              Esta factura está vinculada. Para editarla, primero desvinculala.
            </div>
          </>
        )}

        {/* Panel de confirmación (solo al asignar pendiente; nunca para ya vinculadas) */}
        {!esIgnorada && !esVinculada && confirmando && (
          <div
            style={{
              marginTop: 16,
              padding: "14px 16px",
              background: "#eaf2f8",
              borderRadius: 6,
              border: "1px solid #aed6f1",
              fontSize: "0.82rem",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Confirmar asignación</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 28px" }}>
              <div>
                <strong>DTE Folio:</strong> {factura.folio} — {formatMonto(factura.monto_total)}
              </div>
              <div>
                <strong>Proveedor:</strong> {proveedor?.nombre || factura.razon_social_emisor}
              </div>
              <div>
                <strong>Cuenta ER:</strong> {cuentaLabel || cuentaER}
              </div>
              <div>
                <strong>Tipo:</strong> {tipoContable}
              </div>
            </div>
            {/* Resumen líneas de activo */}
            {tipoContable === "activo" && lineasActivo.length > 0 && (
              <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#555" }}>
                <strong>Líneas de activo:</strong> {lineasActivo.length} línea{lineasActivo.length > 1 ? "s" : ""} —
                Total: {formatMonto(totalLineas)}
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {lineasActivo.map((l, i) => {
                    const tipoLabel = TIPOS_ACTIVO.find((t) => t.value === l.tipo_activo)?.label || l.tipo_activo;
                    return (
                      <li key={i}>
                        {l.cantidad}x {tipoLabel}
                        {l.descripcion ? ` — ${l.descripcion}` : ""}
                        {l.destino_tipo === "edificio"
                          ? ` → ${edificios.find((e) => e.id === l.destino_id)?.edificio || "Edificio"}`
                          : l.destino_tipo === "carteleria"
                            ? ` → ${proyectosCarteleria.find((p) => p.id === l.destino_id)?.nombre_proyecto || "Cartelería"}`
                            : l.destino_tipo === "empleado"
                              ? ` → ${getDestinoOpciones("empleado").find((o) => o.value === l.destino_id)?.label || "Empleado"}`
                              : " → Sin asignar"}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                Pago estimado <span style={{ color: "#e74c3c" }}>*</span>
              </label>
              <SelectorFechaDocumento
                value={plazopago}
                onChange={(nuevaFecha) => setPlazopago(nuevaFecha || "")}
                pais={pais}
                modo={MODOS_VALIDACION.DATO_DOCUMENTO}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: `1px solid ${!plazopago ? "#e74c3c" : "#aed6f1"}`,
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
              <Button color="secondary" outline size="sm" onClick={() => setConfirmando(false)}>
                Volver
              </Button>
              <Button color="dark" size="sm" onClick={handleAsignar} disabled={guardando}>
                {guardando ? "Guardando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {esIgnorada ? (
          <>
            <Button color="secondary" size="sm" onClick={toggle}>
              Cerrar
            </Button>
            <Button color="warning" size="sm" onClick={handleReactivar} disabled={guardando}>
              {guardando ? "..." : "Reactivar"}
            </Button>
          </>
        ) : confirmando ? (
          <Button color="secondary" outline size="sm" onClick={toggle} disabled={guardando}>
            Cerrar
          </Button>
        ) : modoRapido ? (
          renderFooterConIgnorar()
        ) : esVinculadaConClasificacion ? (
          // Vinculada + clasificada (cerrada): solo lectura. Para editar hay que desvincular primero.
          // Confirmación inline (sin window.confirm).
          confirmandoDesvincular ? (
            <>
              <span style={{ fontSize: "0.78rem", color: "#856404", marginRight: "auto" }}>
                ¿Desvincular la clasificación? El pago (si existe) no se toca.
              </span>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => setConfirmandoDesvincular(false)}
                disabled={desvinculando}
              >
                Cancelar
              </Button>
              <Button color="warning" size="sm" onClick={handleDesvincular} disabled={desvinculando}>
                {desvinculando ? "Desvinculando..." : "Sí, desvincular"}
              </Button>
            </>
          ) : (
            <>
              <Button color="secondary" size="sm" onClick={toggle}>
                Cerrar
              </Button>
              <Button color="warning" size="sm" onClick={() => setConfirmandoDesvincular(true)}>
                Desvincular
              </Button>
            </>
          )
        ) : esVinculada ? (
          // Vinculada SIN clasificar (pago existe, falta clasificación): editable, pero sin
          // "Pago estimado" — se guarda directo la clasificación.
          renderFooterConIgnorar(
            <Button
              color="dark"
              size="sm"
              onClick={handleAsignar}
              disabled={guardando || !tipoContable || (tipoContable === "gasto" && !cuentaER)}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </Button>
          )
        ) : (
          renderFooterConIgnorar(
            <Button
              color="dark"
              size="sm"
              onClick={() => setConfirmando(true)}
              disabled={guardando || !tipoContable || (tipoContable === "gasto" && !cuentaER)}
            >
              Asignar
            </Button>
          )
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AsignarRecibidaModal;
